import { flow, getEnv, Instance, types } from "mobx-state-tree";

import { singleItemPatch } from "../api/ApiService";
import { GisCoordinateResponse } from "../../api-models/response/GisCoordinateResponse";
import serializeToQueryParams from "../../helpers/QueryParams";
import { UrlParams } from "../../components/datatable/interfaces";
import { endpoints } from "../api/ApiEndpoints";
import { StorageModel } from "../storage/StorageModel";
import { CoordinateSystem, emptyCoordinateSystem } from "./GisCoordinates";

const GisCoordinatesModel = types.model({
  gisCoordinateDetails: types.optional(types.frozen<CoordinateSystem>(), emptyCoordinateSystem),
  availableGisCoordinates: types.optional(types.array(types.frozen<CoordinateSystem>()), []),
  areGisCoordinatesLoading: types.optional(types.boolean, false),
});

export const GisCoordinatesStorageModel = types
  .compose(GisCoordinatesModel, StorageModel)
  .named("GisCoordinatesStorageModel")
  .actions((self) => {
    return {
      mapResponse(response: GisCoordinateResponse) {
        self.gisCoordinateDetails = new CoordinateSystem(response);
      },
      updateGisCoordinates: flow(function* updateGisCoordinates(gisCoordinatesId: number) {
        const patchData: singleItemPatch = {
          op: "replace",
          path: "/gisCoordinatesId",
          value: gisCoordinatesId,
        };
        const endpoint = endpoints.get("organization");
        return yield getEnv(self).api.patch(endpoint, [patchData]);
      }),
      loadAvailableGisCoordinates: flow(function* getAvailableGisCoordinates(
        queryParams: UrlParams
      ) {
        const serializedQueryParams = serializeToQueryParams(queryParams);
        const endpointGisCoordinates = endpoints.get("gis_coordinates");
        endpointGisCoordinates!.queryParams = serializedQueryParams;

        self.areGisCoordinatesLoading = true;

        const response = yield getEnv(self).api.get(endpointGisCoordinates!);

        self.availableGisCoordinates = response.map(
          (gisCoordinate: GisCoordinateResponse) => new CoordinateSystem(gisCoordinate)
        );
        self.areGisCoordinatesLoading = false;
      }),
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("organization_gis_coordinates");
      return endpoint;
    },
    get isLoading() {
      return self.gisCoordinateDetails.id < 0;
    },
  }));

export type GisCoordinatesStorage = Instance<typeof GisCoordinatesStorageModel>;
